import type { ApiResponse, WordsResponse, Settings } from '../types'

const API_BASE = (import.meta as any).env?.VITE_API_BASE || 'http://localhost:3301/api'
const USER_ID = 'devword_user'
const AUTH_STORAGE_KEY = 'devword_auth'

function originFromApiBase(): string {
  // Relative path (e.g. /api) → same origin as the page
  if (API_BASE.startsWith('/')) {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin
    }
    return ''
  }
  try {
    return new URL(API_BASE).origin
  } catch {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin
    }
    return 'http://localhost:3301'
  }
}

let cachedBaseUrl = originFromApiBase()

export function getBaseUrl(): string {
  return cachedBaseUrl || originFromApiBase()
}

function getAuthToken(): string {
  try {
    const raw = uni.getStorageSync(AUTH_STORAGE_KEY)
    if (!raw) return ''
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!parsed?.token) return ''
    if (typeof parsed.exp === 'number' && Date.now() > parsed.exp) {
      uni.removeStorageSync(AUTH_STORAGE_KEY)
      return ''
    }
    return parsed.token
  } catch {
    return ''
  }
}

function handleUnauthorized() {
  try {
    uni.removeStorageSync(AUTH_STORAGE_KEY)
  } catch {}
  try {
    const pages = getCurrentPages()
    const current = pages.length ? pages[pages.length - 1].route : ''
    if (current !== 'pages/login/index') {
      uni.reLaunch({ url: '/pages/login/index' })
    }
  } catch {
    uni.reLaunch({ url: '/pages/login/index' })
  }
}

async function request<T = any>(url: string, options: RequestInit = {}): Promise<T> {
  const token = getAuthToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-User-Id': USER_ID,
    ...(options.headers as Record<string, string>),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  })

  if (res.status === 401) {
    // Failed login verify returns 401 with a message — do not treat as session expiry
    const isAuthFlow = url.startsWith('/auth/')
    if (!isAuthFlow && token) {
      handleUnauthorized()
      throw new Error('Unauthorized')
    }
    let msg = 'Unauthorized'
    try {
      const body: ApiResponse = await res.json()
      if (body?.msg) msg = body.msg
    } catch {}
    throw new Error(msg)
  }

  if (!res.ok) {
    let msg = `API error: ${res.status}`
    try {
      const body: ApiResponse = await res.json()
      if (body?.msg) msg = body.msg
    } catch {}
    throw new Error(msg)
  }

  const json: ApiResponse<T> = await res.json()
  if (json.code !== 0) throw new Error(json.msg || 'API error')
  return json.data
}

export { request, getAuthToken, handleUnauthorized }

export const api = {
  async getWords(): Promise<WordsResponse> {
    try {
      const data = await request<WordsResponse>('/words')
      if (data?.base_url) cachedBaseUrl = data.base_url
      return data
    } catch (e) {
      console.warn('[API] GET words failed:', e)
      return { base_url: '', items: [] }
    }
  },

  async getWordCount(): Promise<number> {
    try {
      const res = await request<{ count: number }>('/words/count')
      return res.count || 0
    } catch {
      return 0
    }
  },

  async addWord(word: string, translation: string, context?: string): Promise<any> {
    try {
      return await request('/words', {
        method: 'POST',
        body: JSON.stringify({ word, translation, context }),
      })
    } catch (e) {
      console.warn('[API] POST word failed:', e)
      return null
    }
  },

  async updateWord(id: string, data: { word?: string; translation?: string; context?: string }): Promise<any> {
    try {
      return await request(`/words/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
    } catch (e) {
      console.warn('[API] PUT word failed:', e)
      return null
    }
  },

  async deleteWord(id: string): Promise<boolean> {
    try {
      await request(`/words/${id}`, { method: 'DELETE' })
      return true
    } catch (e) {
      console.warn('[API] DELETE word failed:', e)
      return false
    }
  },

  async getSettings(): Promise<Settings | null> {
    try {
      return await request<Settings>('/settings')
    } catch (e) {
      console.warn('[API] GET settings failed:', e)
      return null
    }
  },

  async setSettings(settings: Settings): Promise<boolean> {
    try {
      await request('/settings', {
        method: 'PUT',
        body: JSON.stringify(settings),
      })
      return true
    } catch (e) {
      console.warn('[API] PUT settings failed:', e)
      return false
    }
  },

  async health(): Promise<boolean> {
    try {
      const root = API_BASE.replace(/\/api\/?$/, '')
      const res = await fetch(root ? `${root}/health` : '/health')
      const json = await res.json()
      return json.status === 'ok'
    } catch {
      return false
    }
  },
}
